import { useCallback, useEffect, useRef, useState } from "react";
import Confetti from "react-canvas-confetti/dist/presets/fireworks";
import type { TConductorInstance } from "react-canvas-confetti/dist/types";
import clsx from "clsx";
import { Info } from "lucide-react";

import { Tooltip } from "@/design-system/components/Tooltip/Tooltip";
import { Dialog } from "@/design-system/components/Dialog/Dialog";
import { formatCurrency } from "./utils";
import { formatMonthLong } from "./api";
import { useMoneySaved } from "./MoneySavedContext";

let confettiFired = false;

function SlotMachineDigit({
  digit,
  delay,
  onComplete,
}: {
  digit: string;
  delay: number;
  onComplete?: () => void;
}) {
  const [displayDigit, setDisplayDigit] = useState("0");
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (digit === "," || digit === "$") {
      setDisplayDigit(digit);
      onComplete?.();
      return;
    }

    const targetNum = parseInt(digit, 10);
    if (isNaN(targetNum)) {
      setDisplayDigit(digit);
      onComplete?.();
      return;
    }

    let currentIteration = 0;
    const totalIterations = 15 + Math.floor(Math.random() * 10);
    const intervalTime = 40;

    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        currentIteration++;
        if (currentIteration < totalIterations) {
          setDisplayDigit(Math.floor(Math.random() * 10).toString());
        } else {
          setDisplayDigit(digit);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          onComplete?.();
        }
      }, intervalTime);
    }, delay);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [digit, delay, onComplete]);

  return (
    <span className="inline-block tabular-nums transition-transform">
      {displayDigit}
    </span>
  );
}

function AnimatedCurrency({
  value,
  onAnimationComplete,
}: {
  value: number;
  onAnimationComplete?: () => void;
}) {
  const formatted = formatCurrency(value);
  const [key, setKey] = useState(0);
  const completedCount = useRef(0);
  const totalDigits = formatted.length;
  const hasCalledComplete = useRef(false);

  useEffect(() => {
    setKey((k) => k + 1);
    completedCount.current = 0;
    hasCalledComplete.current = false;
  }, [value]);

  const handleDigitComplete = useCallback(() => {
    completedCount.current++;
    if (completedCount.current >= totalDigits && !hasCalledComplete.current) {
      hasCalledComplete.current = true;
      onAnimationComplete?.();
    }
  }, [totalDigits, onAnimationComplete]);

  return (
    <span className="inline-flex" key={key}>
      {formatted.split("").map((char, index) => (
        <SlotMachineDigit
          key={`${key}-${index}`}
          digit={char}
          delay={index * 60}
          onComplete={handleDigitComplete}
        />
      ))}
    </span>
  );
}

export const MoneyHero = ({
  showExplainability = true,
}: {
  showExplainability?: boolean;
}) => {
  const { totalSavings, months } = useMoneySaved();
  const conductorRef = useRef<TConductorInstance | null>(null);
  const [phase, setPhase] = useState(0);
  const [methodologyOpen, setMethodologyOpen] = useState(false);

  const trackingSince =
    months.length > 0 ? formatMonthLong(months[0].month) : null;

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 200);
    return () => clearTimeout(t1);
  }, []);

  const onConfettiInit = useCallback(
    ({ conductor }: { conductor: TConductorInstance }) => {
      conductorRef.current = conductor;
    },
    [],
  );

  const shootConfetti = useCallback(() => {
    conductorRef.current?.shoot();
  }, []);

  const handleCountComplete = useCallback(() => {
    setPhase(2);
    if (totalSavings > 0 && !confettiFired) {
      confettiFired = true;
      shootConfetti();
      setTimeout(() => shootConfetti(), 400);
      setTimeout(() => shootConfetti(), 800);
    }
  }, [totalSavings, shootConfetti]);

  return (
    <div className="flex flex-col items-center justify-center py-8">
      <Confetti onInit={onConfettiInit} globalOptions={{ resize: true }} />
      <span className="animate-gradient-x relative bg-gradient-to-r from-blue-600 via-purple-500 to-blue-600 bg-[length:200%_auto] bg-clip-text text-7xl font-bold tracking-tight text-transparent drop-shadow-sm md:text-8xl">
        {phase >= 1 ? (
          <AnimatedCurrency
            value={totalSavings}
            onAnimationComplete={handleCountComplete}
          />
        ) : (
          <span className="invisible">{formatCurrency(totalSavings)}</span>
        )}
      </span>
      <span
        className={clsx(
          "text-textSubtle mt-3 flex items-center gap-1.5 text-xl font-medium transition-all duration-500",
          phase >= 2 ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0",
        )}
      >
        Total Saved
        {showExplainability && (
          <Tooltip
            content="Running total of monthly savings since tracking began."
            triggerProps={{ style: { cursor: "default" } }}
          >
            <Info size={14} className="text-slate-400" />
          </Tooltip>
        )}
      </span>
      {showExplainability && (
        <>
          {trackingSince && (
            <span
              className={clsx(
                "text-textSubtle mt-1 text-xs transition-all duration-500",
                phase >= 2
                  ? "translate-y-0 opacity-100"
                  : "translate-y-2 opacity-0",
              )}
            >
              Tracking since {trackingSince}
            </span>
          )}
          <button
            type="button"
            onClick={() => setMethodologyOpen(true)}
            className={clsx(
              "mt-2 cursor-pointer text-xs text-indigo-600 underline transition-all duration-500 hover:text-indigo-800",
              phase >= 2
                ? "translate-y-0 opacity-100"
                : "translate-y-2 opacity-0",
            )}
          >
            How is this calculated?
          </button>
          <Dialog
            open={methodologyOpen}
            onOpenChange={setMethodologyOpen}
            title="How savings are calculated"
          >
            <div className="flex flex-col gap-5 text-sm">
              <div>
                <h4 className="font-semibold">Formula</h4>
                <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-center">
                  <span className="font-serif text-base tracking-wide italic">
                    Savings
                    <sub className="text-xs text-slate-500 not-italic">
                      month
                    </sub>{" "}
                    = <span className="not-italic">(</span>
                    CPA
                    <sub className="text-xs text-slate-500 not-italic">
                      baseline
                    </sub>{" "}
                    <span className="not-italic">−</span> CPA
                    <sub className="text-xs text-slate-500 not-italic">
                      current
                    </sub>
                    <span className="not-italic">)</span>{" "}
                    <span className="not-italic">×</span> (# low-funnel
                    conversions)
                  </span>
                </div>
                <p className="text-textSubtle mt-2 text-center text-xs leading-relaxed">
                  Each month, we compare your current cost per acquisition to
                  your baseline and multiply the difference by the number of
                  low-funnel conversions.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">How is CPA calculated?</h4>
                <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-center">
                  <span className="font-serif text-base tracking-wide italic">
                    CPA ={" "}
                    <span className="not-italic">
                      <sup>Ad Spend</sup>&frasl;
                      <sub># Low-Funnel Conversions</sub>
                    </span>
                  </span>
                </div>
                <p className="text-textSubtle mt-2 text-center text-xs leading-relaxed">
                  Cost per acquisition is your total ad spend for a month
                  divided by the number of low-funnel conversions for that
                  month.
                </p>
              </div>

              <div>
                <h4 className="font-semibold">What is Baseline CPA?</h4>
                <p className="text-textSubtle mt-1.5 leading-relaxed">
                  Your CPA from the first full month after connecting Google
                  CAPI. A month qualifies when it has:
                </p>
                <ul className="text-textSubtle mt-1.5 list-disc space-y-0.5 pl-5">
                  <li>25+ days of ad spend data</li>
                  <li>
                    25+{" "}
                    <a
                      href="https://documentation.freshpaint.io/admin-panel/insights/attribution#getting-started"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cursor-pointer text-indigo-600 underline hover:text-indigo-800"
                    >
                      low-funnel conversions
                    </a>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold">
                  Where does the data come from?
                </h4>
                <p className="text-textSubtle mt-1.5 leading-relaxed">
                  Conversion and spend data come from your{" "}
                  <a
                    href="https://documentation.freshpaint.io/integrations/destinations/direct-response-ads/google-ads-conversion-api/google-ads-conversion-api-quick-start-guide"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cursor-pointer text-indigo-600 underline hover:text-indigo-800"
                  >
                    Google Ads CAPI integration
                  </a>
                  , synced through Freshpaint Insights.
                </p>
              </div>

              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <h4 className="font-semibold text-amber-900">
                  Important caveat
                </h4>
                <ul className="mt-1.5 list-disc space-y-1 pl-5 leading-relaxed text-amber-800">
                  <li>
                    This formula attributes all CPA improvement to Freshpaint's
                    Google CAPI integration. It does not control for
                    seasonality, market changes, or other factors that may also
                    affect ad efficiency.
                  </li>
                  <li>
                    In months where CPA rises above your baseline, savings are
                    reported as $0 rather than a negative number.
                  </li>
                </ul>
              </div>
            </div>
          </Dialog>
        </>
      )}
    </div>
  );
};
